// v3.21-1611 · Marketplace — single-page hub with Browse / My Orders /
// (internal) Orders + Services tabs.  No new sidebar entries; everything
// lives inside `/marketplace`.
import { useEffect, useMemo, useState, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import api from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import useDocumentTitle from '../hooks/useDocumentTitle';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Checkbox } from '../components/ui/checkbox';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../components/ui/dialog';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { Store, ShoppingCart, Package, ClipboardList, Wrench } from 'lucide-react';

const currency = (cents, cur = 'USD') => {
  if (cents == null) return '';
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: cur })
    .format(cents / 100);
};

const purchaseLabel = (svc) => {
  if (!svc) return 'Learn More';
  const mode = svc.purchase_mode || 'inquiry';
  if (mode === 'inquiry')          return 'Inquire About Pricing';
  if (mode === 'order')            return 'Order Now';
  if (mode === 'stripe_one_time')  return 'Buy Now';
  if (mode === 'stripe_recurring') return 'Subscribe';
  return 'Learn More';
};

const priceLabel = (svc) => {
  if (!svc) return '';
  if (svc.price_mode === 'custom')       return 'Custom';
  if (svc.price_mode === 'free')         return 'Free';
  const base = currency(svc.price_cents, svc.currency);
  const suffix = svc.billing_interval === 'month' ? '/mo'
              : svc.billing_interval === 'year'  ? '/yr' : '';
  const prefix = svc.price_mode === 'starting_at' ? 'From ' : '';
  return `${prefix}${base}${suffix}`;
};

const statusBadgeColor = (s) => {
  switch (s) {
    case 'paid':                return 'bg-emerald-100 text-emerald-800';
    case 'unpaid':              return 'bg-amber-100 text-amber-800';
    case 'pending':             return 'bg-sky-100 text-sky-800';
    case 'failed':              return 'bg-red-100 text-red-800';
    case 'refunded':
    case 'partially_refunded':  return 'bg-slate-200 text-slate-700';
    case 'awaiting_payment':    return 'bg-amber-100 text-amber-800';
    case 'payment_processing':  return 'bg-sky-100 text-sky-800';
    case 'in_fulfillment':      return 'bg-indigo-100 text-indigo-800';
    case 'completed':           return 'bg-emerald-100 text-emerald-800';
    case 'cancelled':           return 'bg-slate-200 text-slate-700';
    default:                    return 'bg-slate-200 text-slate-700';
  }
};

// ────────────────────────────────────────────────────────────
// Browse
// ────────────────────────────────────────────────────────────
const BrowseTab = ({ onOpenService }) => {
  const [categories, setCategories] = useState([]);
  const [services, setServices] = useState([]);
  const [activeCat, setActiveCat] = useState('all');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const [c, s] = await Promise.all([
          api.get('/marketplace/categories'),
          api.get('/marketplace/services'),
        ]);
        setCategories(c.data.categories || []);
        setServices(s.data.services || []);
      } catch (e) {
        toast.error('Failed to load marketplace');
      }
      setLoading(false);
    })();
  }, []);

  const visible = useMemo(() => {
    if (activeCat === 'all') return services;
    return services.filter((x) => x.category_id === activeCat);
  }, [services, activeCat]);

  if (loading) return <p className="text-slate-500">Loading marketplace…</p>;

  return (
    <div className="space-y-6" data-testid="marketplace-browse">
      <div className="flex flex-wrap gap-2">
        <button
          onClick={() => setActiveCat('all')}
          data-testid="mp-cat-all"
          className={`px-3 py-1.5 rounded-full text-sm border transition ${
            activeCat === 'all' ? 'bg-slate-900 text-white border-slate-900'
                                : 'bg-white text-slate-700 border-slate-200 hover:border-slate-400'}`}
        >All</button>
        {categories.map((c) => (
          <button
            key={c.id}
            onClick={() => setActiveCat(c.id)}
            data-testid={`mp-cat-${c.slug}`}
            className={`px-3 py-1.5 rounded-full text-sm border transition ${
              activeCat === c.id ? 'bg-slate-900 text-white border-slate-900'
                                 : 'bg-white text-slate-700 border-slate-200 hover:border-slate-400'}`}
          >{c.name}</button>
        ))}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {visible.map((svc) => (
          <div
            key={svc.id}
            className="border border-slate-200 rounded-xl p-5 bg-white flex flex-col gap-3 hover:shadow-md transition"
            data-testid={`mp-service-card-${svc.slug}`}
          >
            <div className="flex items-start justify-between gap-3">
              <h3 className="text-lg font-semibold text-slate-800 leading-tight">{svc.name}</h3>
              <Badge className="bg-slate-100 text-slate-700 shrink-0">{svc.category_name}</Badge>
            </div>
            <p className="text-sm text-slate-600 line-clamp-3">{svc.description}</p>
            <ul className="text-xs text-slate-500 space-y-1 pl-4 list-disc">
              {(svc.features || []).slice(0, 3).map((f, i) => <li key={i}>{f}</li>)}
            </ul>
            <div className="mt-auto flex items-center justify-between pt-3 border-t border-slate-100">
              <span className="text-slate-800 font-semibold" data-testid={`mp-service-price-${svc.slug}`}>
                {priceLabel(svc)}
              </span>
              <Button
                size="sm"
                onClick={() => onOpenService(svc)}
                data-testid={`mp-service-cta-${svc.slug}`}
              >
                {purchaseLabel(svc)}
              </Button>
            </div>
          </div>
        ))}
        {visible.length === 0 && (
          <p className="col-span-full text-slate-500 py-12 text-center">
            No services available in this category.
          </p>
        )}
      </div>
    </div>
  );
};

// ────────────────────────────────────────────────────────────
// Service detail + checkout dialog
// ────────────────────────────────────────────────────────────
const ServiceDialog = ({ open, onClose, service, user, onOrderPlaced, onInquirySent }) => {
  const [stage, setStage] = useState('details');   // details | checkout | inquiry | confirmation
  const [form, setForm] = useState({});
  const [confirmation, setConfirmation] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [idem, setIdem] = useState('');

  useEffect(() => {
    if (open && service) {
      setStage('details');
      setConfirmation(null);
      setForm({
        contact_full_name: user?.full_name || '',
        contact_email:     user?.email || '',
        contact_phone:     user?.phone || '',
        company_name:      user?.company_name || '',
        billing_address:   '',
        billing_city:      '',
        billing_state:     '',
        billing_zip:       '',
        order_notes:       '',
        requested_start_date: '',
        terms_accepted:    false,
        message:           '',
        quantity:          1,
      });
      setIdem(`idem-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`);
    }
  }, [open, service, user]);

  if (!open || !service) return null;

  const isOrderable = ['order', 'stripe_one_time', 'stripe_recurring'].includes(service.purchase_mode)
                    && service.price_mode !== 'custom';

  const submitCheckout = async () => {
    if (!form.terms_accepted) { toast.error('You must accept the terms'); return; }
    setSubmitting(true);
    try {
      const { data } = await api.post('/marketplace/checkout', {
        service_id:           service.id,
        quantity:             Math.max(1, parseInt(form.quantity, 10) || 1),
        contact_full_name:    form.contact_full_name,
        contact_email:        form.contact_email,
        contact_phone:        form.contact_phone,
        company_name:         form.company_name,
        billing_address:      form.billing_address,
        billing_city:         form.billing_city,
        billing_state:        form.billing_state,
        billing_zip:          form.billing_zip,
        order_notes:          form.order_notes,
        requested_start_date: form.requested_start_date || null,
        terms_accepted:       true,
        idempotency_key:      idem,
      });
      setConfirmation(data.order);
      setStage('confirmation');
      onOrderPlaced?.();
      if (data.duplicate) toast.info('Duplicate detected — showing existing order.');
      else toast.success('Order received — awaiting payment processing.');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Checkout failed');
    }
    setSubmitting(false);
  };

  const submitInquiry = async () => {
    setSubmitting(true);
    try {
      await api.post('/marketplace/inquiries', {
        service_id:    service.id,
        message:       form.message,
        contact_email: form.contact_email,
        contact_phone: form.contact_phone,
      });
      toast.success('Inquiry sent — our team will follow up.');
      onInquirySent?.();
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Inquiry failed');
    }
    setSubmitting(false);
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-2xl" data-testid="mp-service-dialog">
        <DialogHeader>
          <DialogTitle>{stage === 'confirmation' ? 'Order Received' : service.name}</DialogTitle>
        </DialogHeader>

        {stage === 'details' && (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <Badge className="bg-slate-100 text-slate-700">{service.category_name}</Badge>
              <span className="text-lg font-semibold text-slate-800">{priceLabel(service)}</span>
            </div>
            <p className="text-slate-700 whitespace-pre-line">{service.long_description || service.description}</p>
            <ul className="text-sm text-slate-600 space-y-1 pl-5 list-disc">
              {(service.features || []).map((f, i) => <li key={i}>{f}</li>)}
            </ul>
            <DialogFooter className="pt-2">
              <Button variant="outline" onClick={onClose}>Close</Button>
              {isOrderable ? (
                <Button onClick={() => setStage('checkout')} data-testid="mp-open-checkout-btn">
                  {purchaseLabel(service)}
                </Button>
              ) : (
                <Button onClick={() => setStage('inquiry')} data-testid="mp-open-inquiry-btn">
                  {purchaseLabel(service)}
                </Button>
              )}
            </DialogFooter>
          </div>
        )}

        {stage === 'inquiry' && (
          <div className="space-y-3" data-testid="mp-inquiry-form">
            <p className="text-sm text-slate-600">
              Pricing for <strong>{service.name}</strong> depends on scope. Tell us a bit about what you need.
            </p>
            <div>
              <label className="text-sm font-medium text-slate-700">Message</label>
              <Textarea
                data-testid="mp-inquiry-message"
                value={form.message}
                onChange={(e) => setForm({ ...form, message: e.target.value })}
                rows={4}
                placeholder="Tell us about your project or requirements…"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-sm font-medium text-slate-700">Email</label>
                <Input value={form.contact_email} onChange={(e) => setForm({ ...form, contact_email: e.target.value })} />
              </div>
              <div>
                <label className="text-sm font-medium text-slate-700">Phone</label>
                <Input value={form.contact_phone} onChange={(e) => setForm({ ...form, contact_phone: e.target.value })} />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setStage('details')}>Back</Button>
              <Button onClick={submitInquiry} disabled={submitting} data-testid="mp-submit-inquiry">
                {submitting ? 'Sending…' : 'Send Inquiry'}
              </Button>
            </DialogFooter>
          </div>
        )}

        {stage === 'checkout' && (
          <div className="space-y-4" data-testid="mp-checkout-form">
            <div className="rounded-lg bg-slate-50 p-3 flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Service</p>
                <p className="font-medium text-slate-800">{service.name}</p>
              </div>
              <span className="text-lg font-semibold text-slate-800" data-testid="mp-checkout-total">
                {priceLabel(service)}
              </span>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-sm font-medium text-slate-700">Name</label>
                <Input value={form.contact_full_name}
                       onChange={(e) => setForm({ ...form, contact_full_name: e.target.value })}
                       data-testid="mp-checkout-name" />
              </div>
              <div>
                <label className="text-sm font-medium text-slate-700">Email</label>
                <Input value={form.contact_email}
                       onChange={(e) => setForm({ ...form, contact_email: e.target.value })}
                       data-testid="mp-checkout-email" />
              </div>
              <div>
                <label className="text-sm font-medium text-slate-700">Phone</label>
                <Input value={form.contact_phone}
                       onChange={(e) => setForm({ ...form, contact_phone: e.target.value })} />
              </div>
              <div>
                <label className="text-sm font-medium text-slate-700">Company</label>
                <Input value={form.company_name}
                       onChange={(e) => setForm({ ...form, company_name: e.target.value })} />
              </div>
              <div className="col-span-2">
                <label className="text-sm font-medium text-slate-700">Billing address</label>
                <Input value={form.billing_address}
                       onChange={(e) => setForm({ ...form, billing_address: e.target.value })} />
              </div>
              <div>
                <label className="text-sm font-medium text-slate-700">City</label>
                <Input value={form.billing_city}
                       onChange={(e) => setForm({ ...form, billing_city: e.target.value })} />
              </div>
              <div>
                <label className="text-sm font-medium text-slate-700">State</label>
                <Input value={form.billing_state}
                       onChange={(e) => setForm({ ...form, billing_state: e.target.value })} />
              </div>
              <div>
                <label className="text-sm font-medium text-slate-700">ZIP</label>
                <Input value={form.billing_zip}
                       onChange={(e) => setForm({ ...form, billing_zip: e.target.value })} />
              </div>
              <div>
                <label className="text-sm font-medium text-slate-700">Requested start date</label>
                <Input type="date" value={form.requested_start_date}
                       onChange={(e) => setForm({ ...form, requested_start_date: e.target.value })} />
              </div>
              <div className="col-span-2">
                <label className="text-sm font-medium text-slate-700">Order notes / special requirements</label>
                <Textarea value={form.order_notes} rows={3}
                          onChange={(e) => setForm({ ...form, order_notes: e.target.value })} />
              </div>
            </div>

            <label className="flex items-start gap-2 text-sm text-slate-700">
              <Checkbox
                checked={form.terms_accepted}
                onCheckedChange={(v) => setForm({ ...form, terms_accepted: !!v })}
                data-testid="mp-checkout-terms"
              />
              <span>
                I accept the terms and conditions for this service.
                Payment for this order will be processed manually by the Prestigious Companies team.
              </span>
            </label>

            <DialogFooter>
              <Button variant="outline" onClick={() => setStage('details')}>Back</Button>
              <Button
                onClick={submitCheckout}
                disabled={submitting || !form.terms_accepted}
                data-testid="mp-place-order-btn"
              >
                {submitting ? 'Placing order…' : 'Place Order'}
              </Button>
            </DialogFooter>
          </div>
        )}

        {stage === 'confirmation' && confirmation && (
          <div className="space-y-3" data-testid="mp-order-confirmation">
            <div className="rounded-lg bg-emerald-50 border border-emerald-200 p-4">
              <p className="text-sm text-emerald-700 font-medium">Order received — awaiting payment processing.</p>
            </div>
            <dl className="grid grid-cols-2 gap-y-2 text-sm text-slate-700">
              <dt className="text-slate-500">Order Number</dt>
              <dd className="font-mono font-medium" data-testid="mp-confirmation-order-number">
                {confirmation.order_number}
              </dd>
              <dt className="text-slate-500">Service</dt>
              <dd>{confirmation.service_snapshot?.name}</dd>
              <dt className="text-slate-500">Amount</dt>
              <dd>{currency(confirmation.total_cents, confirmation.currency)}</dd>
              <dt className="text-slate-500">Payment Status</dt>
              <dd><Badge className={statusBadgeColor(confirmation.payment_status)}>{confirmation.payment_status}</Badge></dd>
              <dt className="text-slate-500">Order Status</dt>
              <dd><Badge className={statusBadgeColor(confirmation.order_status)}>{confirmation.order_status}</Badge></dd>
            </dl>
            <p className="text-sm text-slate-600">
              Our team will contact you shortly to complete payment.  You can track this order in <strong>My Orders</strong>.
            </p>
            <DialogFooter>
              <Button onClick={onClose}>Done</Button>
            </DialogFooter>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

// ────────────────────────────────────────────────────────────
// My Orders (customer)
// ────────────────────────────────────────────────────────────
const MyOrdersTab = ({ refreshKey }) => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const { data } = await api.get('/marketplace/orders');
        setOrders(data.orders || []);
      } catch { toast.error('Failed to load your orders'); }
      setLoading(false);
    })();
  }, [refreshKey]);

  if (loading) return <p className="text-slate-500">Loading orders…</p>;
  if (!orders.length) return <p className="text-slate-500 py-6">You have no orders yet.</p>;

  return (
    <div className="overflow-x-auto" data-testid="mp-my-orders">
      <table className="w-full text-sm border-collapse">
        <thead>
          <tr className="text-left text-slate-500 border-b border-slate-200">
            <th className="py-3 px-3">Order</th>
            <th className="py-3 px-3">Service</th>
            <th className="py-3 px-3">Date</th>
            <th className="py-3 px-3">Total</th>
            <th className="py-3 px-3">Payment</th>
            <th className="py-3 px-3">Status</th>
          </tr>
        </thead>
        <tbody>
          {orders.map((o) => (
            <tr key={o.id} className="border-b border-slate-100" data-testid={`mp-my-order-row-${o.id}`}>
              <td className="py-3 px-3 font-mono text-xs">{o.order_number}</td>
              <td className="py-3 px-3">{o.service_snapshot?.name}</td>
              <td className="py-3 px-3">{o.submitted_at?.slice(0, 10)}</td>
              <td className="py-3 px-3">{currency(o.total_cents, o.currency)}</td>
              <td className="py-3 px-3">
                <Badge className={statusBadgeColor(o.payment_status)}
                       data-testid={`mp-my-order-payment-${o.id}`}>{o.payment_status}</Badge>
              </td>
              <td className="py-3 px-3">
                <Badge className={statusBadgeColor(o.order_status)}
                       data-testid={`mp-my-order-status-${o.id}`}>{o.order_status}</Badge>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

// ────────────────────────────────────────────────────────────
// Admin — orders queue + offline payment recording
// ────────────────────────────────────────────────────────────
const AdminOrdersTab = ({ refreshKey, bumpRefresh }) => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [payingOrder, setPayingOrder] = useState(null);
  const [payForm, setPayForm] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [statusFilter, setStatusFilter] = useState('all');
  const [search, setSearch] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const q = new URLSearchParams();
      if (statusFilter !== 'all') q.set('status', statusFilter);
      if (search) q.set('search', search);
      const { data } = await api.get(`/marketplace/admin/orders?${q}`);
      setOrders(data.orders || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load orders');
    }
    setLoading(false);
  }, [statusFilter, search]);

  useEffect(() => { load(); }, [load, refreshKey]);

  const openPay = (o) => {
    setPayingOrder(o);
    setPayForm({
      payment_status:    'paid',
      payment_method:    'offline_ach',
      payment_reference: '',
      amount_cents:      o.total_cents,
      paid_at:           new Date().toISOString().slice(0, 10),
      internal_note:     '',
    });
  };

  const recordPayment = async () => {
    if (!payingOrder) return;
    setSubmitting(true);
    try {
      await api.post(`/marketplace/admin/orders/${payingOrder.id}/payment`, {
        payment_status:    payForm.payment_status,
        payment_method:    payForm.payment_method,
        payment_reference: payForm.payment_reference || null,
        amount_cents:      parseInt(payForm.amount_cents, 10) || payingOrder.total_cents,
        paid_at:           payForm.paid_at ? new Date(payForm.paid_at).toISOString() : null,
        internal_note:     payForm.internal_note || null,
      });
      toast.success('Payment recorded');
      setPayingOrder(null);
      bumpRefresh?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to record payment');
    }
    setSubmitting(false);
  };

  return (
    <div className="space-y-4" data-testid="mp-admin-orders">
      <div className="flex items-center gap-3">
        <Input placeholder="Search order number / customer / service…"
               value={search}
               onChange={(e) => setSearch(e.target.value)}
               data-testid="mp-admin-search"
               className="max-w-md" />
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="px-3 py-2 border border-slate-200 rounded-md text-sm"
          data-testid="mp-admin-status-filter"
        >
          <option value="all">All statuses</option>
          <option value="awaiting_payment">Awaiting Payment</option>
          <option value="payment_processing">Payment Processing</option>
          <option value="paid">Paid</option>
          <option value="in_fulfillment">In Fulfillment</option>
          <option value="completed">Completed</option>
          <option value="cancelled">Cancelled</option>
          <option value="refunded">Refunded</option>
        </select>
        <Button variant="outline" onClick={load}>Refresh</Button>
      </div>

      {loading ? (
        <p className="text-slate-500">Loading…</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm border-collapse">
            <thead>
              <tr className="text-left text-slate-500 border-b border-slate-200">
                <th className="py-3 px-3">Order</th>
                <th className="py-3 px-3">Customer</th>
                <th className="py-3 px-3">Service</th>
                <th className="py-3 px-3">Total</th>
                <th className="py-3 px-3">Payment</th>
                <th className="py-3 px-3">Status</th>
                <th className="py-3 px-3">Submitted</th>
                <th className="py-3 px-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {orders.map((o) => (
                <tr key={o.id} className="border-b border-slate-100" data-testid={`mp-admin-order-row-${o.id}`}>
                  <td className="py-3 px-3 font-mono text-xs">{o.order_number}</td>
                  <td className="py-3 px-3">
                    {o.contact_full_name || o.user_full_name}<br />
                    <span className="text-xs text-slate-500">{o.contact_email || o.user_email}</span>
                  </td>
                  <td className="py-3 px-3">{o.service_snapshot?.name}</td>
                  <td className="py-3 px-3">{currency(o.total_cents, o.currency)}</td>
                  <td className="py-3 px-3">
                    <Badge className={statusBadgeColor(o.payment_status)}>{o.payment_status}</Badge>
                  </td>
                  <td className="py-3 px-3">
                    <Badge className={statusBadgeColor(o.order_status)}>{o.order_status}</Badge>
                  </td>
                  <td className="py-3 px-3 text-xs text-slate-500">{o.submitted_at?.slice(0, 10)}</td>
                  <td className="py-3 px-3 text-right">
                    <Button size="sm" variant="outline"
                            onClick={() => openPay(o)}
                            data-testid={`mp-admin-record-payment-btn-${o.id}`}>
                      Record Payment
                    </Button>
                  </td>
                </tr>
              ))}
              {orders.length === 0 && (
                <tr><td colSpan={8} className="py-8 text-center text-slate-500">No orders found.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      <Dialog open={!!payingOrder} onOpenChange={(v) => !v && setPayingOrder(null)}>
        <DialogContent className="max-w-lg" data-testid="mp-admin-pay-dialog">
          <DialogHeader>
            <DialogTitle>Record Payment · {payingOrder?.order_number}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <p className="text-xs text-slate-500">
              This records an OFFLINE payment. No raw card data is collected.
            </p>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-sm font-medium">Payment status</label>
                <select className="w-full px-3 py-2 border border-slate-200 rounded-md text-sm"
                        value={payForm.payment_status}
                        onChange={(e) => setPayForm({ ...payForm, payment_status: e.target.value })}
                        data-testid="mp-admin-pay-status">
                  <option value="pending">Pending / Processing</option>
                  <option value="paid">Paid</option>
                  <option value="failed">Failed</option>
                  <option value="refunded">Refunded</option>
                  <option value="partially_refunded">Partially Refunded</option>
                </select>
              </div>
              <div>
                <label className="text-sm font-medium">Payment method</label>
                <select className="w-full px-3 py-2 border border-slate-200 rounded-md text-sm"
                        value={payForm.payment_method}
                        onChange={(e) => setPayForm({ ...payForm, payment_method: e.target.value })}
                        data-testid="mp-admin-pay-method">
                  <option value="offline_ach">Offline · ACH</option>
                  <option value="offline_wire">Offline · Wire</option>
                  <option value="offline_check">Offline · Check</option>
                  <option value="offline_cash">Offline · Cash</option>
                  <option value="offline_other">Offline · Other</option>
                </select>
              </div>
              <div>
                <label className="text-sm font-medium">Reference #</label>
                <Input value={payForm.payment_reference}
                       onChange={(e) => setPayForm({ ...payForm, payment_reference: e.target.value })}
                       data-testid="mp-admin-pay-reference" />
              </div>
              <div>
                <label className="text-sm font-medium">Amount (¢)</label>
                <Input value={payForm.amount_cents}
                       onChange={(e) => setPayForm({ ...payForm, amount_cents: e.target.value })}
                       data-testid="mp-admin-pay-amount" />
              </div>
              <div>
                <label className="text-sm font-medium">Paid on</label>
                <Input type="date" value={payForm.paid_at}
                       onChange={(e) => setPayForm({ ...payForm, paid_at: e.target.value })} />
              </div>
            </div>
            <div>
              <label className="text-sm font-medium">Internal note</label>
              <Textarea rows={2}
                        value={payForm.internal_note}
                        onChange={(e) => setPayForm({ ...payForm, internal_note: e.target.value })} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPayingOrder(null)}>Cancel</Button>
            <Button onClick={recordPayment} disabled={submitting} data-testid="mp-admin-pay-submit">
              {submitting ? 'Recording…' : 'Save Payment'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// ────────────────────────────────────────────────────────────
// Root page
// ────────────────────────────────────────────────────────────
const MarketplacePage = () => {
  useDocumentTitle('Marketplace');
  const { user, isAdmin, isInternal } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState(searchParams.get('tab') || 'browse');
  const [openService, setOpenService] = useState(null);
  const [refreshOrders, setRefreshOrders] = useState(0);

  useEffect(() => {
    const t = searchParams.get('tab') || 'browse';
    setActiveTab(t);
  }, [searchParams]);

  const changeTab = (t) => {
    setActiveTab(t);
    setSearchParams({ tab: t });
  };

  const isInternalUser = isInternal?.() || (user?.company_type || '').toLowerCase() === 'internal';
  const isAdminUser    = isAdmin?.();

  return (
    <div className="p-6 space-y-6" data-testid="marketplace-page">
      <div>
        <h1 className="text-3xl font-bold text-slate-900 flex items-center gap-2">
          <Store className="h-7 w-7 text-slate-700" />
          Marketplace
        </h1>
        <p className="text-slate-500">Services, orders, and fulfillment — all in one place.</p>
      </div>

      <Tabs value={activeTab} onValueChange={changeTab}>
        <TabsList>
          <TabsTrigger value="browse" data-testid="mp-tab-browse">
            <Package className="h-4 w-4 mr-2" /> Browse
          </TabsTrigger>
          <TabsTrigger value="my-orders" data-testid="mp-tab-my-orders">
            <ShoppingCart className="h-4 w-4 mr-2" /> My Orders
          </TabsTrigger>
          {isInternalUser && (
            <TabsTrigger value="admin-orders" data-testid="mp-tab-admin-orders">
              <ClipboardList className="h-4 w-4 mr-2" /> Orders
            </TabsTrigger>
          )}
          {isAdminUser && (
            <TabsTrigger value="admin-services" data-testid="mp-tab-admin-services">
              <Wrench className="h-4 w-4 mr-2" /> Services
            </TabsTrigger>
          )}
        </TabsList>

        <TabsContent value="browse" className="mt-6">
          <BrowseTab onOpenService={setOpenService} />
        </TabsContent>
        <TabsContent value="my-orders" className="mt-6">
          <MyOrdersTab refreshKey={refreshOrders} />
        </TabsContent>
        {isInternalUser && (
          <TabsContent value="admin-orders" className="mt-6">
            <AdminOrdersTab refreshKey={refreshOrders} bumpRefresh={() => setRefreshOrders((n) => n + 1)} />
          </TabsContent>
        )}
        {isAdminUser && (
          <TabsContent value="admin-services" className="mt-6">
            <AdminServicesTab />
          </TabsContent>
        )}
      </Tabs>

      <ServiceDialog
        open={!!openService}
        service={openService}
        user={user}
        onClose={() => setOpenService(null)}
        onOrderPlaced={() => setRefreshOrders((n) => n + 1)}
        onInquirySent={() => {}}
      />
    </div>
  );
};

// ────────────────────────────────────────────────────────────
// Admin — services editor (admin only)
// ────────────────────────────────────────────────────────────
const AdminServicesTab = () => {
  const [services, setServices] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    const [c, s] = await Promise.all([
      api.get('/marketplace/categories'),
      api.get('/marketplace/services?active_only=false'),
    ]);
    setCategories(c.data.categories || []);
    setServices(s.data.services || []);
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    setSaving(true);
    try {
      const body = {
        category_id:       editing.category_id,
        name:              editing.name,
        slug:              editing.slug,
        description:       editing.description,
        long_description:  editing.long_description,
        image_url:         editing.image_url,
        purchase_mode:     editing.purchase_mode,
        price_mode:        editing.price_mode,
        price_cents:       editing.price_cents !== '' && editing.price_cents != null
                            ? parseInt(editing.price_cents, 10)
                            : null,
        currency:          editing.currency || 'USD',
        billing_interval:  editing.billing_interval || null,
        features:          (editing.features || []),
        terms_url:         editing.terms_url,
        stripe_product_id: editing.stripe_product_id || null,
        stripe_price_id:   editing.stripe_price_id   || null,
        sort_order:        parseInt(editing.sort_order, 10) || 0,
        active:            editing.active !== false,
      };
      if (editing.id) {
        await api.patch(`/marketplace/admin/services/${editing.id}`, body);
      } else {
        await api.post('/marketplace/admin/services', body);
      }
      toast.success('Service saved');
      setEditing(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Save failed');
    }
    setSaving(false);
  };

  if (loading) return <p className="text-slate-500">Loading…</p>;

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <h3 className="text-lg font-semibold">Services ({services.length})</h3>
        <Button onClick={() => setEditing({
          category_id: categories[0]?.id || '',
          name: '', slug: '', purchase_mode: 'order', price_mode: 'fixed',
          price_cents: 0, currency: 'USD', billing_interval: 'month',
          features: [], sort_order: 0, active: true,
        })} data-testid="mp-admin-new-service">+ New Service</Button>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="text-left text-slate-500 border-b border-slate-200">
            <tr>
              <th className="py-2 px-3">Name</th>
              <th className="py-2 px-3">Category</th>
              <th className="py-2 px-3">Mode</th>
              <th className="py-2 px-3">Price</th>
              <th className="py-2 px-3">Active</th>
              <th className="py-2 px-3 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {services.map((s) => (
              <tr key={s.id} className="border-b border-slate-100">
                <td className="py-2 px-3 font-medium">{s.name}</td>
                <td className="py-2 px-3">{s.category_name}</td>
                <td className="py-2 px-3">{s.purchase_mode}</td>
                <td className="py-2 px-3">{priceLabel(s)}</td>
                <td className="py-2 px-3">{s.active ? 'Yes' : 'No'}</td>
                <td className="py-2 px-3 text-right">
                  <Button size="sm" variant="outline" onClick={() => setEditing({ ...s })}>Edit</Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Dialog open={!!editing} onOpenChange={(v) => !v && setEditing(null)}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>{editing?.id ? 'Edit Service' : 'New Service'}</DialogTitle>
          </DialogHeader>
          {editing && (
            <div className="grid grid-cols-2 gap-3">
              <div className="col-span-2">
                <label className="text-sm font-medium">Name</label>
                <Input value={editing.name || ''} onChange={(e) => setEditing({ ...editing, name: e.target.value })} />
              </div>
              <div>
                <label className="text-sm font-medium">Slug</label>
                <Input value={editing.slug || ''} onChange={(e) => setEditing({ ...editing, slug: e.target.value })} />
              </div>
              <div>
                <label className="text-sm font-medium">Category</label>
                <select className="w-full px-3 py-2 border border-slate-200 rounded-md text-sm"
                        value={editing.category_id || ''}
                        onChange={(e) => setEditing({ ...editing, category_id: e.target.value })}>
                  {categories.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
              </div>
              <div>
                <label className="text-sm font-medium">Purchase mode</label>
                <select className="w-full px-3 py-2 border border-slate-200 rounded-md text-sm"
                        value={editing.purchase_mode}
                        onChange={(e) => setEditing({ ...editing, purchase_mode: e.target.value })}>
                  <option value="inquiry">Inquiry</option>
                  <option value="order">Order (offline pay)</option>
                  <option value="stripe_one_time">Stripe · One-time</option>
                  <option value="stripe_recurring">Stripe · Recurring</option>
                </select>
              </div>
              <div>
                <label className="text-sm font-medium">Price mode</label>
                <select className="w-full px-3 py-2 border border-slate-200 rounded-md text-sm"
                        value={editing.price_mode}
                        onChange={(e) => setEditing({ ...editing, price_mode: e.target.value })}>
                  <option value="custom">Custom</option>
                  <option value="fixed">Fixed</option>
                  <option value="starting_at">Starting at</option>
                  <option value="free">Free</option>
                </select>
              </div>
              <div>
                <label className="text-sm font-medium">Price (¢)</label>
                <Input value={editing.price_cents ?? ''}
                       onChange={(e) => setEditing({ ...editing, price_cents: e.target.value })} />
              </div>
              <div>
                <label className="text-sm font-medium">Billing interval</label>
                <select className="w-full px-3 py-2 border border-slate-200 rounded-md text-sm"
                        value={editing.billing_interval || ''}
                        onChange={(e) => setEditing({ ...editing, billing_interval: e.target.value })}>
                  <option value="">—</option>
                  <option value="one_time">One time</option>
                  <option value="month">Monthly</option>
                  <option value="year">Yearly</option>
                </select>
              </div>
              <div className="col-span-2">
                <label className="text-sm font-medium">Short description</label>
                <Input value={editing.description || ''}
                       onChange={(e) => setEditing({ ...editing, description: e.target.value })} />
              </div>
              <div className="col-span-2">
                <label className="text-sm font-medium">Long description</label>
                <Textarea rows={3} value={editing.long_description || ''}
                          onChange={(e) => setEditing({ ...editing, long_description: e.target.value })} />
              </div>
              <div className="col-span-2 flex items-center gap-2">
                <Checkbox checked={editing.active !== false}
                          onCheckedChange={(v) => setEditing({ ...editing, active: !!v })} />
                <span className="text-sm">Active</span>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditing(null)}>Cancel</Button>
            <Button onClick={save} disabled={saving}>{saving ? 'Saving…' : 'Save'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default MarketplacePage;
